import React from 'react';
import { Grid } from '@mui/material';
import useStore from '../Store/Store';
import RecipientsSelector from '../CanCare/Components/RecipientsSelector';

/**
 * Dynamic PageHeader component that handles different page header patterns
 *
 * @param {Object} props - Component props
 * @param {string} props.title - Page title to display
 * @param {boolean} props.showRecipientSelector - Whether to show recipient selector (default: true)
 * @param {React.ReactNode} props.actionButton - Action button to display on the right side
 * @param {React.ReactNode} props.breadcrumbs - Breadcrumb navigation component
 * @param {Object} props.containerStyle - Additional styles for the container
 *
 * @example
 * // Basic usage with title and recipient selector
 * <PageHeader title="Health Overview" />
 *
 * @example
 * // With action button
 * <PageHeader
 *   title="Caregivers"
 *   actionButton={<Button text="Add Caregiver" onClick={handleAdd} />}
 * />
 *
 * @example
 * // Without recipient selector (for pages like Care Recipients)
 * <PageHeader
 *   title="Care Recipients"
 *   showRecipientSelector={false}
 *   actionButton={<Button text="Invite Recipients" onClick={handleInvite} />}
 * />
 *
 * @example
 * // With breadcrumbs
 * <PageHeader
 *   title="Recipient Details"
 *   breadcrumbs={
 *     <span>
 *       <span onClick={() => history.goBack()}>Care recipients</span>
 *       <span> / {recipientName}</span>
 *     </span>
 *   }
 * />
 *
 * @example
 * // For pages like Calendar that have different layout
 * <PageHeader
 *   title="Calendar"
 *   containerStyle={{ width: '90%', left: '12%' }}
 * />
 */
const PageHeader = ({
  title,
  showRecipientSelector = true,
  actionButton = null,
  breadcrumbs = null,
  containerStyle = {},
}) => {
  const width = useStore((state) => state.screenWidth);
  const careRecipients = useStore((state) => state.careRecipients);

  // Determine if recipient selector should be shown
  const shouldShowRecipientSelector = showRecipientSelector && Object.keys(careRecipients).length > 0;

  return (
    <>
      {/* Breadcrumbs */}
      {breadcrumbs && <div style={{ marginBottom: '10px' }}>{breadcrumbs}</div>}

      {/* Recipient Selector */}
      {shouldShowRecipientSelector && (
        <div style={{ width: '100%', marginBottom: '20px' }}>
          <RecipientsSelector />
        </div>
      )}

      {/* Page Title and Action Button */}
      {(title || actionButton) && (
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          direction={width > 550 ? 'row' : 'column'}
          style={{
            marginBottom: '20px',
            marginTop: shouldShowRecipientSelector ? '10px' : '0px',
          }}
        >
          {title && (
            <Grid item>
              <h1>{title}</h1>
            </Grid>
          )}
          {actionButton && <Grid item>{actionButton}</Grid>}
        </Grid>
      )}
    </>
  );
};

export default PageHeader;
